import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { gsap } from 'gsap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import { PercentsService } from '@common/services/percents.service';
import { NumbersService } from '@common/services/numbers.service';

@Component({
    selector: 'aa-gauge',
    templateUrl: './gauge.component.html',
    styleUrls: [
        './gauge.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GaugeComponent implements OnInit, OnChanges, OnDestroy
{
    private destroyator = new Subject();
    @Input() width: number;
    @Input() value: number;
    @Input() total: number;
    @Input() height: number;
    @Input() fontColor: string;
    @Input() backColor: string;
    @Input() foreColor: string;
    @Input('legend')
    set legend (value: string)
    {
        this.leftLegend = value;
    }
    public pxSvgHeight: string;
    public pxGaugeHeight: string;
    public radius: number;
    public gaugePos: string;
    public leftLegendPos: string;
    public rightLegendPos: string;
    public pxOuterWidth: string;
    public pxInnerWidth: string;
    public pxSvgWidth: string;
    public viewBox: string;
    public rightLegend: string;
    public leftLegend: string;
    private svgWidth: number;
    private outerWidth: number;
    private percent: number;
    private innerGauge: SVGElement;
    private duration: number = 0.5;

    constructor (
        private translateService: TranslateService,
        private numbersService: NumbersService,
        private percentsService: PercentsService,
        private elementRef: ElementRef
    )
    { }

    ngOnInit ()
    {
        const doubleHeight = 4 * this.height;

        this.innerGauge = this.elementRef.nativeElement.querySelector('rect#innerGauge');

        this.svgWidth = this.width;
        this.pxSvgWidth = this.toPx(this.svgWidth);

        this.outerWidth = this.svgWidth;
        this.pxOuterWidth = this.toPx(this.outerWidth);

        this.viewBox = `0 0 ${this.svgWidth} 10`;

        this.pxSvgHeight = this.toPx(6 * this.height);
        this.pxGaugeHeight = this.toPx(this.height);
        this.radius = this.height / 2;

        this.gaugePos = `translate(0, ${doubleHeight})`;

        this.leftLegendPos = 'translate(0, 0)';
        this.rightLegendPos = `translate(${this.outerWidth}, 0)`;

        this.moveGauge();
        this.translateService.onLangChange
            .pipe(
                tap(() =>
                    this.formatRightLegend()
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnChanges (changes: SimpleChanges): void
    {
        this.value = parseInt(changes.value.currentValue, 10);
        this.moveGauge();
    }

    ngOnDestroy (): void
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    private moveGauge ()
    {
        this.percent = 100 * this.value / this.total;
        const w: number = this.percent / 100 * this.outerWidth;

        gsap.timeline()
            .to(
                this.innerGauge,
                {
                    duration: this.duration,
                    width: w,
                    ease: 'sine'
                });
        this.formatRightLegend();
    }

    private formatRightLegend (): void
    {
        const v = this.numbersService.toFixed(this.value, 0);
        const p = this.percentsService.percentage(this.percent / 100, 2);

        this.rightLegend = `${v} - ${p}`;
    }

    private toPx (v: number): string
    {
        return `${v}px`;
    }
}
